import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Drawer } from 'aha-components';
import { isMobile } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { NumericFormat } from 'react-number-format';
import PriceFieldsComponent from './PriceFieldsComponent';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import {
  hasSubmittedScheduleConflict,
  isCreatorScheduleConflictValid
} from '@/utils/creatorScheduleConflict';
import { OrderStatus } from '@/interface/order';
import axiosInstance from '../../../apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { MOBILE_DRAWER_HEIGHT } from '@/utils/viewportHeight';
import { areScreeningQuestionsValid } from './ScreeningQuestions/validation';
import { scrollElementToTop } from '@/utils/scrollElementToTop';
import {
  resolveAhaPicksOfferItem,
  resolveAcceptOfferDisplayPrice
} from './AhaPicksPrePriceFields';

interface NumberFormatUSDProps {
  inputRef: (instance: any) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const {
    showModalConfirmDealPrice,
    setShowModalConfirmDealPrice,
    setShowModalAcceptAhaPicksOffer
  } = useWebModalStore();
  const { detailData } = useDetailDataStore();

  if (!detailData?.order?.status.includes(OrderStatus.Invited)) {
    return null;
  }

  return (
    <>
      <Drawer
        width={810}
        title={t('modal.confirm_deal_price.title', {
          defaultValue: 'Confirm deal price'
        })}
        open={showModalConfirmDealPrice}
        onClose={() => {
          setShowModalConfirmDealPrice(false);
          setShowModalAcceptAhaPicksOffer(false);
        }}
        bodyStyle={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        placement={isMobile() ? 'bottom' : 'right'}
        height={isMobile() ? MOBILE_DRAWER_HEIGHT : undefined}
      >
        <AnalysisContent />
      </Drawer>
    </>
  );
};

const AnalysisContent: React.FC = () => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    showModalConfirmDealPrice,
    setShowModalConfirmDealPrice,
    setShowModalMissOut,
    setShowModalSubmitInterest,
    showModalAcceptAhaPicksOffer
  } = useWebModalStore();
  const {
    selectedPriceFields,
    setSelectedPriceFields,
    setRateNonNegotiable,
    screeningQuestions,
    setScreeningQuestions,
    triggerPriceErrorShake,
    creatorScheduleConflict,
    resetCreatorScheduleConflict
  } = useMultiplePricesStore();

  const [newOrderPrice, setNewOrderPrice] = useState<number | string>('');
  const [nextLoading, setNextLoading] = useState(false);
  const [nonNegotiable, setNonNegotiable] = useState(false);
  const [isPriceRangeValid, setIsPriceRangeValid] = useState(true);
  const [modalSession, setModalSession] = useState(0);
  const scheduleConflictSubmitted = hasSubmittedScheduleConflict(
    detailData?.order?.scheduleConflict
  );
  // 抽屉刚打开时需要重置 answer；detail 刷新时要保留已填内容
  const resetScreeningAnswersOnSyncRef = useRef(false);

  const isScreeningQuestionsValid = useMemo(() => {
    const campaignScreeningQuestions = detailData?.campaign?.screeningQuestions;
    if (!campaignScreeningQuestions?.length) return true;
    if (screeningQuestions.length !== campaignScreeningQuestions.length) {
      return false;
    }
    return areScreeningQuestionsValid(screeningQuestions);
  }, [detailData?.campaign?.screeningQuestions, screeningQuestions]);

  // 仅在抽屉打开/关闭时重置本地表单 session，避免 detail 刷新 remount 表单
  useEffect(() => {
    if (!showModalConfirmDealPrice) {
      setNewOrderPrice('');
      setNonNegotiable(false);
      setIsPriceRangeValid(true);
      resetScreeningAnswersOnSyncRef.current = false;
      return;
    }

    setModalSession((session) => session + 1);
    const currentOrder = useDetailDataStore.getState().detailData?.order;
    if (!hasSubmittedScheduleConflict(currentOrder?.scheduleConflict)) {
      resetCreatorScheduleConflict();
    }
    resetScreeningAnswersOnSyncRef.current = true;
  }, [showModalConfirmDealPrice, resetCreatorScheduleConflict]);

  // 同步 screening questions：
  // - 刚打开抽屉：用空 answer 初始化
  // - 打开后 questions 晚到：补初始化
  // - detail 刷新：保留已填 answer，避免被清空
  useEffect(() => {
    if (!showModalConfirmDealPrice) return;

    const campaignScreeningQuestions = detailData?.campaign?.screeningQuestions;
    if (!campaignScreeningQuestions?.length) {
      // campaign 已加载且确实没有题目时才清空；detail 未就绪时不要动
      if (detailData?.campaign) {
        setScreeningQuestions([]);
        resetScreeningAnswersOnSyncRef.current = false;
      }
      return;
    }

    const existing = useMultiplePricesStore.getState().screeningQuestions;
    const shouldResetAnswers = resetScreeningAnswersOnSyncRef.current;
    resetScreeningAnswersOnSyncRef.current = false;

    setScreeningQuestions(
      campaignScreeningQuestions.map((question: any, index: number) => ({
        ...question,
        answer: shouldResetAnswers
          ? (question?.answer ?? '')
          : (existing[index]?.answer ?? question?.answer ?? '')
      }))
    );
  }, [
    showModalConfirmDealPrice,
    detailData?.campaign,
    detailData?.campaign?.screeningQuestions,
    setScreeningQuestions
  ]);

  // 价格默认值可随 offer / order 更新，但不重置 screening questions
  useEffect(() => {
    if (!showModalConfirmDealPrice) return;

    if (showModalAcceptAhaPicksOffer) {
      const selected = useMultiplePricesStore.getState().selectedPriceFields[0];
      const offerItem =
        selected ??
        resolveAhaPicksOfferItem(
          useDetailDataStore.getState().detailData?.order
        );
      const acceptPrice = resolveAcceptOfferDisplayPrice(offerItem);
      if (acceptPrice != null) {
        setNewOrderPrice(acceptPrice);
      }
      return;
    }

    const fixedCreatorEarnAmount =
      useDetailDataStore.getState().detailData?.order?.fixedCreatorEarnAmount;
    if (fixedCreatorEarnAmount) {
      setNewOrderPrice(fixedCreatorEarnAmount);
      setNonNegotiable(true);
    }
  }, [
    showModalConfirmDealPrice,
    showModalAcceptAhaPicksOffer,
    detailData?.order?.fixedCreatorEarnAmount,
    detailData?.order?.id
  ]);

  const nextClick = async () => {
    if (!isPriceRangeValid) {
      triggerPriceErrorShake();
      return;
    }
    if (
      !isScreeningQuestionsValid ||
      (!scheduleConflictSubmitted &&
        !isCreatorScheduleConflictValid(creatorScheduleConflict))
    )
      return;

    if (showModalAcceptAhaPicksOffer) {
      setShowModalConfirmDealPrice(false);
      setShowModalSubmitInterest(true);
      return;
    }

    if (newOrderPrice && +newOrderPrice > 0 && selectedPriceFields.length > 0) {
      const params = {
        interestedPriceField: {
          ...selectedPriceFields[0],
          actualGet: +newOrderPrice
        }
      };
      setNextLoading(true);
      try {
        const { data } = await axiosInstance.post(
          `/order/${detailData?.order?.id}/competitivePrice`,
          params
        );
        // 等待价格接口期间仍可编辑答案，跳转前重新读取并校验当前值。
        const currentStore = useMultiplePricesStore.getState();
        const currentQuestions = currentStore.screeningQuestions;
        const expectedQuestionCount =
          useDetailDataStore.getState().detailData?.campaign?.screeningQuestions
            ?.length ?? 0;
        if (
          currentQuestions.length !== expectedQuestionCount ||
          !areScreeningQuestionsValid(currentQuestions)
        ) {
          currentStore.setScreeningQuestionsError(true);
          scrollElementToTop('take-order-screening-questions');
          return;
        }
        setSelectedPriceFields([data?.data?.interestedPriceField]);
        setRateNonNegotiable(nonNegotiable);
        setShowModalConfirmDealPrice(false);
        setShowModalSubmitInterest(true);
      } catch (error) {
        console.log('error', error);
      } finally {
        setNextLoading(false);
      }
    } else {
      setShowModalConfirmDealPrice(false);
      setShowModalSubmitInterest(true);
    }
  };

  return (
    <div className="w-full flex flex-col items-start gap-3 flex-1 min-h-0">
      <div className="shrink-0 w-full">
        <ModalAnalysisBreadcrumb index={1} />
      </div>
      <div className="w-full flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto">
        <div className="text-[#1c2024] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
          {t('modal.confirm_deal_price.select_content_type', {
            defaultValue: 'Select content type'
          })}{' '}
        </div>
        <PriceFieldsComponent
          key={modalSession}
          direction={isMobile() ? 'flex flex-col' : 'flex flex-row'}
          hasRadioButton={true}
          orderPriceAdjustment={true}
          newOrderPrice={newOrderPrice}
          setNewOrderPrice={setNewOrderPrice}
          nonNegotiable={nonNegotiable}
          setNonNegotiable={setNonNegotiable as (v: boolean) => void}
          isModal={true}
          onPriceRangeValidChange={setIsPriceRangeValid}
        />
      </div>
      {/*  */}
      <div
        className={`bg-white w-full flex items-center gap-4 border-t border-solid border-[#e8e8ec] pt-4 shrink-0
            ${isMobile() ? 'justify-center pb-[calc(16px+env(safe-area-inset-bottom,0px))]' : 'justify-end'}
          `}
      >
        <Button
          className="!w-[160px] !h-10"
          type="default"
          onClick={() => {
            setShowModalConfirmDealPrice(false);
            setShowModalMissOut(true);
            setScreeningQuestions([]);
          }}
        >
          {t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          className="!w-[160px] !h-10"
          type="primary"
          disabled={
            nextLoading ||
            !newOrderPrice ||
            !isScreeningQuestionsValid ||
            (!scheduleConflictSubmitted &&
              !isCreatorScheduleConflictValid(creatorScheduleConflict))
          }
          onClick={nextClick}
          loading={nextLoading}
        >
          {t('common.next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </div>
  );
};

export default ModalShowAnInterest;
